import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { CheckCircle2, ArrowUpRight, Loader2, Package, ShieldCheck } from "lucide-react";

export default function Products() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [starters, setStarters] = useState([]);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/starters").then((r) => setStarters(r.data)).catch(() => {});
  }, []);

  const buyStarter = async (sp) => {
    if (!user) { navigate("/login", { state: { from: "/products" } }); return; }
    setBuying(sp.id);
    try {
      const { data } = await api.post("/checkout/starter", { starter_id: sp.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setBuying(null);
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20">
      <Seo
        title="Dealership Toolkits & Operating Systems | Centennial"
        description="Curated toolkits and starter packs of dealership SOPs, forms and training — from $49, with a 30-day money-back guarantee. One system. Every department."
        canonicalPath="/products"
      />
      <div className="max-w-2xl">
        <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Products</span>
        <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter mt-3">
          Solve one problem today — or systemize the whole store.
        </h1>
        <p className="text-muted-foreground mt-5 text-lg">
          Start with a focused solution pack, then build up to the complete operating system. Every tier includes everything below it.
        </p>
      </div>

      {/* SOLUTION PACKS */}
      {starters.length > 0 && (
        <section className="mt-16" data-testid="solution-packs">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            <h2 className="font-heading font-black text-2xl lg:text-3xl tracking-tight">Solution packs — $49 each</h2>
          </div>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            A specific problem, solved end-to-end: the process, the manager procedure, the working form, the checklist, and a quick-reference — curated for you, not a pile of files to sort through.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {starters.map((sp) => (
              <div key={sp.id} data-testid={`starter-card-${sp.id}`} className="flex flex-col border border-border bg-white p-7">
                <h3 className="font-heading font-bold text-lg leading-snug">{sp.name}</h3>
                {sp.problem && <p className="text-sm text-muted-foreground mt-2 italic">"{sp.problem}"</p>}
                <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mt-5">What's inside</div>
                <ul className="mt-2 space-y-1.5 flex-1">
                  {sp.includes?.slice(0, 5).map((t, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground/85">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" /> <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-baseline justify-between mt-6">
                  <div className="font-heading font-black text-2xl">$49</div>
                  <span className="text-xs text-muted-foreground">{sp.doc_count} documents</span>
                </div>
                <button onClick={() => buyStarter(sp)} disabled={buying === sp.id} data-testid={`starter-buy-${sp.id}`}
                  className="mt-4 inline-flex items-center justify-center gap-2 bg-primary text-white py-3 font-semibold btn-lift disabled:opacity-60">
                  {buying === sp.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Get this pack <ArrowUpRight className="w-4 h-4" /></>}
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-primary" /> 30-day money-back guarantee on every pack.
          </p>
        </section>
      )}

      {/* TIERS */}
      <section className="mt-20">
        <h2 className="font-heading font-black text-2xl lg:text-3xl tracking-tight">Or get the whole system</h2>
        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          {products.map((p) => (
            <div key={p.id} data-testid={`product-card-${p.id}`}
              className={`flex flex-col bg-white border p-8 ${p.tier_level === 2 ? "border-primary border-2 shadow-xl shadow-primary/5" : "border-border"}`}>
              {p.badge && (
                <span className={`self-start text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 mb-5 ${p.tier_level === 2 ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                  {p.badge}
                </span>
              )}
              <h2 className="font-heading font-bold text-2xl tracking-tight">{p.short_name}</h2>
              <p className="text-sm text-muted-foreground mt-2">{p.tagline}</p>
              <div className="font-heading font-black text-4xl mt-6">
                {p.id === "cdos" && <span className="text-base font-sans font-normal text-muted-foreground">from </span>}
                ${p.price.toLocaleString()}
                {p.id !== "cdos" && <span className="text-base font-sans font-normal text-muted-foreground"> one-time</span>}
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {p.features?.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 className="w-4.5 h-4.5 text-primary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to={`/products/${p.slug}`} data-testid={`product-cta-${p.id}`}
                className={`mt-8 inline-flex items-center justify-center gap-2 py-3.5 font-semibold btn-lift ${p.tier_level === 2 ? "bg-primary text-white" : "border border-border hover:border-primary hover:text-primary"}`}>
                {p.id === "cdos" ? "Request access" : "View details"} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
